import { gql } from "@apollo/client";
import { client } from "../apolloClient";
import { useQuery, useMutation } from "@apollo/client";

/**
 * useRoleApi custom hook for role operations.
 * - Provides CRUD operations for roles using Apollo Client and GraphQL.
 * - Handles cache updates for role queries and mutations.
 * - Exposes helpers for reading/writing/updating roles in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting roles.
 * - Supports fetching by role ID.
 * - Handles optimistic UI updates and error logging.
 */

// GraphQL Queries and Mutations
const ROLES_QUERIES = {
    // Query to fetch all roles
    GET_ROLES: gql`
      query GetRoles {
        GetRoles {
          role_id
          role_cat_id
          role_name
          role_description
          permissions
          is_active
          is_project
          created_on
          updated_on
        }
      }
    `,

    // Mutation to add a new role
    ADD_ROLE: gql`
      mutation Mutation($roleCatId: Int!, $roleName: String!, $isProject: Boolean, $roleDescription: String, $permissions: JSON) {
        AddRole(role_cat_id: $roleCatId, role_name: $roleName, is_project: $isProject, role_description: $roleDescription, permissions: $permissions) {
          role_id
          role_cat_id
          role_name
          role_description
          permissions
          is_active
          is_project
          created_on
          updated_on
        }
      }
    `,

    // Mutation to update an existing role
    UPDATE_ROLE: gql`
      mutation UpdateRole($roleId: Int!, $roleName: String, $permissions: JSON, $roleDescription: String, $roleCatId: Int, $isProject: Boolean) {
        UpdateRole(role_id: $roleId, role_name: $roleName, permissions: $permissions, role_description: $roleDescription, role_cat_id: $roleCatId, is_project: $isProject) {
          role_id
          role_cat_id
          role_name
          role_description
          permissions
          is_active
          is_project
          created_on
          updated_on
        }
      }
    `,

    // Mutation to delete a role
    DELETE_ROLE: gql`
      mutation DeleteRole($roleId: Int!) {
        DeleteRole(role_id: $roleId) {
          role_id
          role_cat_id
          role_name
          role_description
          permissions
          is_active
          is_project
          created_on
          updated_on
        }
      }
    `,
};

// Cache operations for roles
const cacheOperations = {
    /**
     * Writes roles to Apollo cache.
     * @param {Array} roles
     */
    writeRoles: (roles) => {
        if (!roles) return;
        client.writeQuery({
            query: ROLES_QUERIES.GET_ROLES,
            data: {
                GetRoles: roles,
            },
        });
    },

    /**
     * Reads roles from Apollo cache.
     * @returns {Array}
     */
    readRoles: () => {
        try {
            const { GetRoles } =
                client.readQuery({
                    query: ROLES_QUERIES.GET_ROLES,
                }) || {};
            return GetRoles || [];
        } catch {
            return [];
        }
    },

    /**
     * Updates a role in Apollo cache.
     * @param {Object} updatedRole
     */
    updateRolesInCache: (updatedRole) => {
        const existingRoles = cacheOperations.readRoles();
        const updatedRoles = existingRoles.map((role) =>
            role.role_id === updatedRole.role_id ? updatedRole : role
        );
        cacheOperations.writeRoles(updatedRoles);
    },
};

export const useRoleApi = () => {
    const [addRoleMutation] = useMutation(ROLES_QUERIES.ADD_ROLE);
    const [updateRoleMutation] = useMutation(ROLES_QUERIES.UPDATE_ROLE);

    /**
     * Fetches all roles.
     * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
     */
    const getRoles = async () => {
        try {
            const { data } = await client.query({
                query: ROLES_QUERIES.GET_ROLES,
                fetchPolicy: "network-only",
            });
            return { success: true, data: data.GetRoles };
        } catch (error) {
            console.error("Error fetching roles:", error);
            return { success: false, error: error.message };
        }
    };

    /**
     * Fetches a role by its ID.
     * @param {number|string} role_id
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const getRoleById = async (role_id) => {
        try {
            const { data } = await client.query({
                query: ROLES_QUERIES.GET_ROLE_BY_ID,
                variables: { role_id },
                fetchPolicy: "network-only",
            });
            return { success: true, data: data.GetRoleById };
        } catch (error) {
            console.error("Error fetching role:", error);
            return { success: false, error: error.message };
        }
    };

    /**
     * Creates a new role.
     * @param {Object} formData
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const createRole = async (formData) => {
        try {
            const { data, errors } = await addRoleMutation({
                variables: {
                    roleName: formData.role_name,
                    roleCatId: parseInt(formData.role_cat_id),
                    roleDescription: formData.role_description,
                    permissions: { access: formData.permissions },
                    isActive: formData.is_active,
                    isProject: formData.is_project,
                },
            });

            if (errors?.length > 0) {
                throw new Error(errors[0].message);
            }

            if (!data?.AddRole) {
                throw new Error("Failed to create role");
            }

            return {
                success: true,
                data: data.AddRole,
            };
        } catch (error) {
            // Handle GraphQL errors
            if (error.graphQLErrors?.length > 0) {
                const graphQLError = error.graphQLErrors[0];
                throw new Error(graphQLError.message);
            }

            // Handle network errors
            if (error.networkError?.result?.errors?.length > 0) {
                const networkError = error.networkError.result.errors[0];
                throw new Error(networkError.message);
            }

            throw error;
        }
    };

    /**
     * Updates an existing role.
     * @param {number|string} roleId
     * @param {Object} formData
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const updateRole = async (roleId, formData) => {
        try {
            const { data, errors } = await updateRoleMutation({
                variables: {
                    roleId: parseInt(roleId),
                    roleName: formData.role_name,
                    roleCatId: parseInt(formData.role_cat_id),
                    permissions: { access: formData.permissions },
                    roleDescription: formData.role_description,
                    isActive: formData.is_active,
                    isProject: formData.is_project,
                },
            });

            if (errors?.length > 0) {
                throw new Error(errors[0].message);
            }

            if (!data?.UpdateRole) {
                throw new Error("Failed to update role");
            }

            return {
                success: true,
                data: data.UpdateRole,
            };
        } catch (error) {
            // Handle GraphQL errors
            if (error.graphQLErrors?.length > 0) {
                const graphQLError = error.graphQLErrors[0];
                throw new Error(graphQLError.message);
            }

            // Handle network errors
            if (error.networkError?.result?.errors?.length > 0) {
                const networkError = error.networkError.result.errors[0];
                throw new Error(networkError.message);
            }

            throw error;
        }
    };

    /**
     * Deletes a role by its ID.
     * @param {number|string} role_id
     * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
     */
    const deleteRole = async (role_id) => {
        try {
            const { data } = await client.mutate({
                mutation: ROLES_QUERIES.DELETE_ROLE,
                variables: { roleId: role_id },
            });

            const existingRoles = cacheOperations.readRoles();
            cacheOperations.writeRoles(
                existingRoles.filter((role) => role.role_id !== role_id)
            );

            return { success: true, data: data.DeleteRole };
        } catch (error) {
            console.error("Error deleting role:", error);
            return { success: false, error: error.message };
        }
    };

    return {
        getRoles,
        getRoleById,
        createRole,
        updateRole,
        deleteRole,
        cacheOperations,
    };
};
